'use client'
import { useEffect, useState } from 'react'

/**
 * Hook to detect media query matches with SSR-safe initial state
 *
 * IMPORTANT: To prevent hydration mismatches:
 * - Initial value is `false` on both server and client
 * - Actual media query value is set after component mounts
 * - Components using this hook should handle the brief flash or use CSS for initial state
 *
 * Returns an object with:
 * - value: The current media query match result
 * - mounted: Whether the component has mounted (use to show skeleton/loading state)
 */
export function useMediaQuery(query: string): { value: boolean; mounted: boolean } {
  // Always start with false to ensure server/client match
  const [value, setValue] = useState(false)
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    // Check if window is available (client-side only)
    if (typeof window === 'undefined' || !window.matchMedia) {
      return
    }

    const mediaQuery = window.matchMedia(query)

    // Set initial value
    setValue(mediaQuery.matches)

    // Mark as mounted after value is set
    setMounted(true)

    // Handle changes
    const onChange = (event: MediaQueryListEvent) => {
      setValue(event.matches)
    }

    mediaQuery.addEventListener('change', onChange)

    return () => mediaQuery.removeEventListener('change', onChange)
  }, [query])

  return { value: mounted ? value : false, mounted }
}
